import { useId, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TokenInput, {
  TokenConfig,
  TokenInstance,
} from '@/components/ui/custom/dataInput/TokenInput';
import { FillInTheBlankFormFields } from './FillInTheBlankForm';
import { TFunction } from 'i18next';
import Fieldset from '@/components/ui/daisy/fieldset';
import { countBlanks } from '@/utils/questions/countBlanks';
import { BLANK_PATTERN } from '@/utils/questions/parseFillInTheBlank';

export default function BlankTextAreaField() {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.fillInTheBlankForm',
  });

  const { control, setValue, getValues } =
    useFormContext<FillInTheBlankFormFields>();
  const {
    field: { value, onChange, ref: fieldRef },
    fieldState: { error },
  } = useController({
    control,
    name: 'question',
    rules: {
      validate: input => validateQuestion(input, t),
    },
  });

  const fieldId = useId();
  const errorMessage = error?.message;

  const tokenConfig = useMemo<TokenConfig>(
    () => ({
      blank: {
        match: BLANK_PATTERN,
        serialize: '___',
        render: ({ index }) => ({
          text: t('blankBadge', {
            number: String(index).padStart(2, '0'),
          }),
          className: 'btn btn-soft btn-xs pointer-events-none',
        }),
      },
    }),
    [t],
  );

  const onTokensChange = (tokens: TokenInstance[]) => {
    const tokensSet = new Set(tokens.map(token => token.index));
    const options = getValues('options');
    options.forEach((option, i) => {
      const filtered = option.validPositions.filter(position =>
        tokensSet.has(position),
      );

      if (filtered.length !== option.validPositions.length) {
        setValue(`options.${i}.validPositions`, filtered);
      }
    });
  };

  return (
    <Fieldset labelAlt={errorMessage}>
      <label
        className="flex flex-col gap-4 text-base font-semibold"
        htmlFor={fieldId}
      >
        {t('questionLabel')}
        <Fieldset.Label className="text-xs font-normal text-base-content/60">
          {t('questionHint')}
        </Fieldset.Label>
      </label>
      <TokenInput
        id={fieldId}
        ref={fieldRef}
        value={value ?? ''}
        onTokensChange={onTokensChange}
        onChange={onChange}
        tokenConfig={tokenConfig}
        placeholder={t('questionPlaceholder')}
        error={!!errorMessage}
      />
    </Fieldset>
  );
}

const validateQuestion = (input: string, t: TFunction) => {
  const raw = (input ?? '').trim();
  if (raw.length === 0) return t('questionRequired');
  if (countBlanks(input ?? '') === 0) return t('atLeastOneBlankRequired');
  return true;
};
